<template>
  <BaseDrawer no-padding height-auto-only no-toolbar no-bottom-rounding class="scrollbar-hide"
  @close="$emit('close')">
    <template #content>
      <div class="w-full" style="height: calc(100vh - 80px)">
        <!-- Toolbar -->
        <div class="px-4 py-3 border-b border-border-normal">
          <div class="p-1.5 flex items-center gap-2">
              <PlanIcon class="text-icon-muted" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Choose a Plan
            </BaseText>
            <BaseText size="sm" class="text-text-normal">Upgrade to unlock your account and access all features.</BaseText>
          </div>
        </div>
        <!-- Main -->
        <div class="flex items-center justify-center w-full h-full overflow-y-scroll "
          style="height: calc(100vh - 145px)">
          <div class="p-6 max-w-5xl h-full mt-9">
            <div v-if="isLegacyUser" class="p-4 flex items-center gap-3 bg-background-loud rounded-2xl">
              <CouponIcon class="w-5 h-5 text-secondary-green-100" />
              <div class="flex items-center gap-1">
                <BaseText type="label" size="sm" class="text-white">Legacy User Bonus:</BaseText>
                <BaseText size="sm" class="text-neutral-alpha-800">Enjoy an additional lifetime 10% discount</BaseText>
                <BaseText size="xs" class="text-neutral-alpha-650">(Applied automatically on upgrade)</BaseText>
              </div>
            </div>
            <div v-else-if="activeInterval !== 'monthly' || isSaleActive">
            <div class="p-4 flex items-center justify-between w-full text-secondary-green-300 rounded-2xl relative"
              style="background-color: #effefa" v-if="activeInterval === 'monthly' && isSaleActive">
              <button  class="absolute -top-1 -right-1 rounded-full bg-secondary-green-50 text-secondary-green-200" @click="manualDisableSale = true"><TagRemoveIcon/></button>
              <div class="flex items-center gap-3">
                <CouponIcon class="w-5 h-5" />
                <div class="flex items-center gap-1">
                  <BaseText type="label" size="sm">50% of your first month.</BaseText>
                  <BaseText size="sm">Upgrade to "Workflow" or "Agency" plans for 50% of your first month.</BaseText>
                </div>
              </div>
              <div class="grid grid-cols-5 gap-0.5 text-secondary-green-300 place-items-center">
                <AlarmClockIcon class="col-span-1" />
                <BaseText type="label" size="sm" class="col-span-4">
                  {{ formattedTime }}
                </BaseText>
              </div>
            </div>
            <div class="p-4 flex items-center gap-3 bg-background-normal rounded-2xl" v-else>
              <MoneyCircleIcon class="money-icon w-5 h-5" />
              <div class="flex items-center gap-1">
                <BaseText type="label" size="sm" class="text-text-muted">Annual Plan Bonus</BaseText>
                <BaseText size="sm" class="flex-1 text-text-normal">The only way to get unlimited Spyder brands and save
                  15%</BaseText>
              </div>
            </div>
          </div>
            <!-- Header -->
            <div class="flex items-center justify-between place-items-end my-9">
              <div>
                <BaseText type="label" class="text-text-muted">
                  Choose a Plan
                </BaseText>
                <BaseText size="sm" class="text-text-normal">
                  Upgrade to unlock your account and access all features.
                </BaseText>
              </div>
              <!-- toggle -->
              <div>
                <div class="relative bg-background-normal rounded-lg flex items-center gap-2 p-0.5"
                  ref="intervalContainer">
                  <div :style="backgroundStyle" class="absolute z-10 rounded-lg transition-all selected-interval-tag" />
                  <button ref="monthly-btn" class="px-2 py-1.5 text-text-disabled rounded-lg z-20 transition-colors"
                    @click="setInterval('monthly')" :class="{ ' text-text-muted': activeInterval === 'monthly' }">
                    <BaseText type="label" size="sm">
                      Monthly
                    </BaseText>
                  </button>
                  <button ref="annual-btn"
                    class="px-2 py-1.5 text-text-disabled z-20 transition-colors flex items-center gap-2"
                    @click="setInterval('annual')">
                    <BaseText type="label" size="sm"
                      :class="activeInterval === 'annual' ? 'text-text-muted' : 'text-text-normal'">
                      Annually
                    </BaseText>
                    <BaseText size="sm" :class="activeInterval === 'annual' ? 'text-text-muted' : 'text-text-subdued'">
                      Save 15% & Unlimited Spyder
                    </BaseText>
                  </button>
                </div>
              </div>
            </div>
            <div class="flex items-end justify-center w-full gap-7">
              <ProductCard v-for="plan in SUBSCRIPTION_PLANS" :key="plan.key" :product="plan"
                :is-monthly="activeInterval === 'monthly'" :all-products="PRODUCT_ASSETS" :loadingId="loadingPlan"
                @createSession="createCheckoutSession" @changePlan="promptPlanUpdate" :showDiscount="isSaleActive" :activePlan="activeSubscriptionKey" :activeInterval="activeSubscriptionInterval" :isLegacyUser="isLegacyUser"/>
            </div>
            <div class="grid grid-cols-12 place-items-center mt-10">
              <div v-for="guarantee in GUARANTEE_DETAILS" :key="guarantee.title" class="col-span-4 space-y-1">
                <div class="flex items-center gap-1">
                  <component :is="guarantee.icon?.default || guarantee.icon" class="text-text-muted" />
                  <BaseText type="label" size="sm" class="text-text-muted">
                    {{ guarantee.title }}
                  </BaseText>
                </div>
                <BaseText size="xs" class="text-text-subdued">
                  {{ guarantee.desc }}
                </BaseText>
              </div>
            </div>
            <div class="flex items-center justify-center w-full my-10">
              <button class="base-btn" style="width: 218px" @click="openPage('https://www.foreplay.co/pricing')">
                <BaseText type="label" size="sm">
                  Compare Plans
                </BaseText>
              </button>
            </div>
            <div class="flex gap-5 p-5 rounded-lg border border-border-hover">
              <div>
                <BaseText type="label" class="text-text-muted">
                  Enterprise
                </BaseText>
                <BaseText size="sm" class="text-text-normal">
                  Need a custom plan? We can help you out!
                </BaseText>
                <div class="flex items-center gap-1 mt-2.5">
                  <MoneyCircleIcon class="money-icon" />
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Save up to 80%
                  </BaseText>
                </div>
                <button class="base-btn w-full mt-6" style="width: 218px" @click="openPage('https://www.foreplay.co/book-demo')">
                  <BaseText type="label" size="sm">
                    Book a Call
                  </BaseText>
                </button>
              </div>
              <div class="w-full">
                <div class="space-y-2.5">
                  <div v-for="enterpriseBenefit in ENTERPRISE_BENEFITS" :key="enterpriseBenefit"
                    class="w-full flex items-center gap-3">
                    <BaseText type="label" size="sm" class="text-text-muted flex-1">
                      {{ enterpriseBenefit }}
                    </BaseText>
                    <CheckmarkIcon class="text-text-muted" />
                  </div>
                </div>
                <BaseText type="label" size="sm" class="text-text-muted my-4">
                  Trusted by over 10,000 growth teams and agencies
                </BaseText>
                <div class="flex items-center justify-between">
                  <div v-for="company in TRUSTED_COMPANIES" :key="company">
                    <img :src="company" style="width:auto; max-height: 26px">
                  </div>
                </div>
              </div>
            </div>
            <!-- space -->
            <div class="h-5 w-full" />
          </div>
        </div>
      </div>
      <!-- Modals -->
       <ChangePlanModal :product="changeToPlan" v-if="changeToPlan !== null" :loading="loadingUpdate" @close="changeToPlan = null" :allProducts="PRODUCT_ASSETS" @updateSubscription="handleSubscriptionChange"/>
       <UpdatePaymentMethodModal v-if="showChangePayment" @close="showChangePayment = false"/>
    </template>
  </BaseDrawer>
</template>

<script>
import foreplayAPI from '@/api/foreplayServer'
import {mapActions, mapGetters} from "vuex";

import {SUBSCRIPTION_PLANS, GUARANTEE_DETAILS, PRODUCT_ASSETS} from '@/utils/stripe/productMeta'
// Components
import ProductCard from './ProductCard.vue'
import ChangePlanModal from './ChangePlanModal.vue';
import UpdatePaymentMethodModal from "./UpdatePaymentMethodModal.vue"
// Icons
import CouponIcon from '../../globals/Icons/CouponIcon.vue'
import ForeplayIcon from '../../globals/Icons/ForeplayIcon.vue'
import MoneyCircleIcon from '../../globals/Icons/MoneyCircleIcon.vue'
import CheckmarkIcon from '../../globals/Icons/CheckmarkIcon.vue'
import MoneyBackIcon from '../../globals/Icons/MoneyBackIcon.vue'
import ShieldCancelIcon from "../../globals/Icons/ShieldCancelIcon.vue"
import ChatSupportIcon from "../../globals/Icons/ChatSupportIcon.vue"
import AlarmClockIcon from "../../globals/Icons/AlarmClockIcon.vue"
import PlanIcon from "../../globals/Icons/PlanIcon.vue"
import TagRemoveIcon from "../../globals/Icons/TagRemoveIcon.vue"
export default {
  name: 'SubscriptionDrawer',
  components: {
    ProductCard,
    ChangePlanModal,
    UpdatePaymentMethodModal,
    // Icons
    CouponIcon,
    MoneyCircleIcon,
    CheckmarkIcon,
    ForeplayIcon,
    MoneyBackIcon,
    ShieldCancelIcon,
    AlarmClockIcon,
    PlanIcon,
    ChatSupportIcon,
    TagRemoveIcon,
  },
  props: {
    initialInterval: {
      type: String,
      default: 'monthly'
    }
  },
  data() {
    const ENTERPRISE_BENEFITS = [
      'Unlimited users & product usage',
      'Priority in-app or Slack support',
      'Early access to new features & integrations'
    ]
    const TRUSTED_COMPANIES = [
      require('../../../assets/images/canva-logo.png'),
      require('../../../assets/images/gr0-logo.png'),
      require('../../../assets/images/hello-fresh-logo.png'),
      require('../../../assets/images/lunar-logo.png'),
      require('../../../assets/images/ag1-logo.png'),
      require('../../../assets/images/common-logo.png'),
      require('../../../assets/images/hexclad-logo.png')
    ]


    return {
      ENTERPRISE_BENEFITS,
      GUARANTEE_DETAILS,
      TRUSTED_COMPANIES,
      SUBSCRIPTION_PLANS,
      PRODUCT_ASSETS,

      activeInterval: null,
      backgroundStyle: {},

      loadingPlan: null,
      loadingUpdate: false,

      manualDisableSale: false,
      // Countdown
      timeLeft: 0,
      intervalId: null,

      changeToPlan: null,
      showChangePayment: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserSubscription', 'getIsLegacy', 'getUserOnboardingStatus']),
    isLegacyUser(){
      return this.getIsLegacy
    },
    activeSubscriptionKey(){
      return this.getUserSubscription.internalProductId || null
    },
    activeSubscriptionInterval(){
      return this.getUserSubscription.interval === 'month' ? 'monthly' : 'annual'
    },
    isSaleActive() {
      return !this.activeSubscriptionKey && !this.isLegacyUser && this.timeLeft > 0 && !this.manualDisableSale
    },
    formattedTime() {
      const hours = String(Math.floor(this.timeLeft / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((this.timeLeft % 3600) / 60)).padStart(2, '0');
      const seconds = String(this.timeLeft % 60).padStart(2, '0');
      return `${hours} : ${minutes} : ${seconds}`;
    }
  },
  mounted() {
    this.activeInterval = this.initialInterval || 'monthly'
    this.setInterval(this.activeInterval)
    this.startCountdown()

    // Check onboarding status (duct tape fix - a fallback to ensure the user is onboarded)
    const onboardingCompleted = this.getUserOnboardingStatus === 'FINISH'
    if (!onboardingCompleted && !this.guestStatus?.isGuestOnly) {
      this.$emit('close')
      this.$router.push({ name: 'UserOnboardView' })
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId);
  },
  methods: {
    ...mapActions('AuthModule', ['fetchUserSubscription']),
    openPage(url){
      window.open(url, "_blank")
    },
    async handleSubscriptionChange(newSub){
      const {priceId, _key, addOn} = newSub || {}
      this.loadingUpdate = true;
      try{
        const updateRes = await foreplayAPI.Subscription.changeSubscription(priceId, addOn)
        this.changeToPlan = null;
        this.$showAlert({type: 'success', message: 'Your subscription plan has been successfully updated!'})
      }catch(e){
        console.error(e)
        const errorType = e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : 'An unexpected error occurred. You have not been charged.';
        if (errorType === 'payment_method_required') {
          this.$showAlert({type: 'error', message: e.response?.data?.message})
          // Close modal & show payment update modal
          this.changeToPlan = null
          this.showChangePayment = true
        } else {
          console.error(e);
          this.$showAlert({ type: 'error', message: 'An unexpected error occurred. Please try again later.' });
        }
      }finally{
        this.loadingUpdate = false
      }
    },
    promptPlanUpdate(newPlan){
      this.changeToPlan = newPlan;
    },
    startCountdown() {
      const savedEndTime = localStorage.getItem('fpCountdown');

      let endTime;
      if (savedEndTime) {
        endTime = parseInt(savedEndTime, 10);
      } else {
        endTime = Date.now() + 10 * 60 * 1000; // 10 minutes timer 
        localStorage.setItem('fpCountdown', endTime);
      }

      const update = () => {
        const now = Date.now();
        const diff = Math.floor((endTime - now) / 1000);
        this.timeLeft = Math.max(diff, 0);

        if (this.timeLeft <= 0) {
          clearInterval(this.intervalId);
          // localStorage.removeItem('countdownEndTime'); 
        }
      };

      update(); 
      this.intervalId = setInterval(update, 1000);
    },
    async createCheckoutSession(checkout) {
      const { priceId, key, addOn } = checkout
      this.loadingPlan = key
      try {
        const checkoutRes = await foreplayAPI.Subscription.createCheckout(priceId, addOn, this.isSaleActive)
        const popup = window.open(checkoutRes.url, '_blank')
        if (!popup){
          this.$showAlert({ type: 'error', message: 'Popup blocked. Please allow popups for this site.' })
          return
        }

      } catch (e) {
        console.error(e)
        const errMessage = e.response?.data?.message || 'Unable to generate a checkout session'
        const errCode = e.response?.data?.error || 'unknown_error'
        if (errCode === 'subscription_exists'){
          await this.fetchUserSubscription(true)
          this.$emit('close')
        }
        this.$showAlert({ type: 'error', message: errMessage })
      } finally {
        this.loadingPlan = null
      }
    },
    setInterval(key) {
      this.activeInterval = key;
      this.$nextTick(() => {
        const activeBtn = this.$refs?.[`${this.activeInterval}-btn`];
        const container = this.$refs.intervalContainer;

        if (activeBtn && container) {
          const btnRect = activeBtn.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          this.backgroundStyle = {
            left: `${btnRect.left - containerRect.left}px`,
            top: `${btnRect.top - containerRect.top}px`,
            width: `${btnRect.width}px`,
            height: `${btnRect.height}px`,
          };
        }
      });
    },
  }
}
</script>

<style scoped>
.base-btn {
  padding: 8px 10px;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  color: #303546;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.scrollable-section::-webkit-scrollbar {
  width: 3px;
}

.scrollable-section::-webkit-scrollbar-thumb {
  background-color: #dfe1e7;
  border-radius: 18px;
}

.money-icon {
  color: #EBBE7A
}

.selected-interval-tag {
  border-radius: 6px;
  background-color: white;
  box-shadow: 0px 24px 24px -12px rgba(47, 53, 71, 0.12), 0px 12px 12px -6px rgba(47, 53, 71, 0.04), 0px 3px 3px -1.5px rgba(47, 53, 71, 0.04), 0px 1px 1px -0.5px rgba(47, 53, 71, 0.04), 0px 0px 2px -0.25px rgba(47, 53, 71, 0.08);
}
</style>
